/**
 * `ExportMenu` — small dropdown that offers ".md" and ".json" download
 * triggers for a report. Used by the System Health board and the
 * Test Suite view; the parent supplies the two download callbacks and
 * the menu just renders the affordance.
 *
 * Plain `<details>`/`<summary>` would do the same job, but the styled
 * button + popover gives us the same look on every platform and lets
 * the items inherit the admin's mono typography for "EXPORT .MD" /
 * "EXPORT .JSON".
 *
 * Keyboard: Escape closes the menu, focus returns to the trigger.
 * Outside-click also closes. Mobile: the popover anchors right-aligned
 * to the trigger and shrinks naturally inside its parent flex row.
 */

import { Download } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';

import { useC } from '../../lib/theme.js';

const monoFont = "'JetBrains Mono', monospace";
const sansFont = "'Geist', sans-serif";

export interface ExportMenuProps {
  /** Disabled when there's nothing to export (e.g. before the first
   *  successful fetch). */
  disabled?: boolean;
  onExportMarkdown: () => void;
  onExportJson: () => void;
  /** Optional label override — defaults to "Export report". */
  label?: string;
}

export function ExportMenu({
  disabled = false,
  onExportMarkdown,
  onExportJson,
  label = 'Export report',
}: ExportMenuProps): JSX.Element {
  const C = useC();
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  const toggle = useCallback((): void => {
    if (disabled) return;
    setOpen((p) => !p);
  }, [disabled]);

  const close = useCallback((): void => {
    setOpen(false);
  }, []);

  // Close on outside-click + Escape. Both register only while the
  // menu is open so we don't carry idle listeners.
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent): void => {
      if (wrapperRef.current === null) return;
      if (e.target instanceof Node && wrapperRef.current.contains(e.target)) return;
      close();
    };
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        close();
        triggerRef.current?.focus();
      }
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, close]);

  const handlePick = useCallback(
    (which: 'md' | 'json') => (): void => {
      close();
      if (which === 'md') onExportMarkdown();
      else onExportJson();
    },
    [close, onExportMarkdown, onExportJson],
  );

  return (
    <div ref={wrapperRef} style={{ position: 'relative', display: 'inline-block' }}>
      <button
        ref={triggerRef}
        type="button"
        onClick={toggle}
        disabled={disabled}
        aria-haspopup="menu"
        aria-expanded={open}
        className="w-full sm:w-auto inline-flex items-center justify-center gap-2"
        style={{
          background: 'transparent',
          color: disabled ? C.dim : C.muted,
          border: `1px solid ${C.border}`,
          borderRadius: 2,
          padding: '10px 14px',
          minHeight: 40,
          fontFamily: monoFont,
          fontSize: 11,
          letterSpacing: 1.2,
          fontWeight: 500,
          textTransform: 'uppercase',
          cursor: disabled ? 'not-allowed' : 'pointer',
        }}
      >
        <Download size={13} aria-hidden="true" />
        {label}
      </button>
      {open ? (
        <div
          role="menu"
          aria-label={label}
          style={{
            position: 'absolute',
            top: 'calc(100% + 4px)',
            right: 0,
            minWidth: 160,
            background: C.surface,
            border: `1px solid ${C.border}`,
            borderRadius: 2,
            boxShadow: '0 8px 24px rgba(0,0,0,0.3)',
            zIndex: 40,
            padding: 4,
          }}
        >
          <MenuItem label="Export .md" hint="Markdown" onClick={handlePick('md')} />
          <MenuItem label="Export .json" hint="Raw JSON" onClick={handlePick('json')} />
        </div>
      ) : null}
    </div>
  );
}

function MenuItem({
  label,
  hint,
  onClick,
}: {
  label: string;
  hint: string;
  onClick: () => void;
}): JSX.Element {
  const C = useC();
  return (
    <button
      type="button"
      role="menuitem"
      onClick={onClick}
      className="flex w-full items-center justify-between"
      style={{
        background: 'transparent',
        border: 'none',
        padding: '10px 10px',
        minHeight: 40,
        cursor: 'pointer',
        color: C.text,
        borderRadius: 2,
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = C.surface2;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = 'transparent';
      }}
    >
      <span
        style={{
          fontFamily: monoFont,
          fontSize: 11,
          letterSpacing: 1,
          textTransform: 'uppercase',
        }}
      >
        {label}
      </span>
      <span
        style={{
          fontFamily: sansFont,
          fontSize: 11,
          color: C.dim,
        }}
      >
        {hint}
      </span>
    </button>
  );
}
